import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Plus, Printer, Trash2 } from 'lucide-react';
import type { DrugSafetyWarning } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateTime, humanize } from '@/lib/utils';
import { printDocument } from '@/lib/print';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Field, Input, Select, StatusBadge, Textarea, Tooltip } from '@/components/ui/primitives';
import { ReasonDialog } from '@/components/ui/reason-dialog';
import { useToast } from '@/components/ui/toast';
import { DrugSearchInput } from '../DrugSearchInput';
import { RxSetsMenu } from '../RxSetsMenu';
import { useAuthorisation } from '../AuthorisationContext';
import { SourceStateBadge } from '../SourceStateBadge';
import { ROUTE_OPTIONS, emptyDraft, isNamed, missingRoute, toPrescriptionItem, withForm, type PrescriptionDraft } from '../prescription-draft';
import type { Encounter } from './types';

export function PrescriptionEditor({
  encounter,
  readOnly,
  onIssued,
}: {
  encounter: Encounter;
  readOnly: boolean;
  onIssued: () => void;
}) {
  const toast = useToast();
  const authorisation = useAuthorisation();
  const [items, setItems] = useState<PrescriptionDraft[]>([emptyDraft()]);
  const [notes, setNotes] = useState('');
  const [warnings, setWarnings] = useState<DrugSafetyWarning[]>([]);
  const [acknowledged, setAcknowledged] = useState<string[]>([]);
  const [cancelling, setCancelling] = useState<string | null>(null);

  const named = useMemo(() => items.filter(isNamed), [items]);
  const withoutRoute = missingRoute(items);

  useEffect(() => {
    if (named.length === 0) {
      setWarnings([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setWarnings(
          await post<DrugSafetyWarning[]>('/prescriptions/safety-check', {
            patientId: encounter.patient.id,
            items: named.map((item) => ({ drugName: item.drugName, productId: item.productId })),
          }),
        );
      } catch {
        setWarnings([]);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [named, encounter.patient.id]);

  const issue = useMutation({
    mutationFn: () =>
      post<{ id: string }>('/prescriptions', {
        patientId: encounter.patient.id,
        encounterId: encounter.id,
        items: named.map(toPrescriptionItem),
        notes: notes || undefined,
        acknowledgedWarnings: acknowledged,
      }),
    onSuccess: async (prescription) => {
      toast.success(tr('opd', 'prescription_issued'), tr('opd', 'it_is_now_in_the_pharmacy_queue'));
      setItems([emptyDraft()]);
      setNotes('');
      setAcknowledged([]);
      onIssued();
      const doc = await get<{ html: string }>(`/prescriptions/${prescription.id}/print`);
      printDocument(doc.html);
    },
    onError: (error) => toast.error(tr('opd', 'could_not_issue_the_prescription'), describeError(error)),
  });

  const severe = warnings.filter((w) => w.severity === 'severe');
  const blocked = severe.some((w) => !acknowledged.includes(w.message));

  return (
    <div className="space-y-4">
      {warnings.length > 0 && (
        <Alert variant={severe.length ? 'destructive' : 'warning'} title={tr('opd', 'safety_checks')}>
          <ul className="mt-1 space-y-1">
            {warnings.map((warning) => (
              <li key={warning.message} className="flex items-start gap-2 text-sm">
                <Badge variant={warning.severity === 'severe' ? 'destructive' : 'warning'}>{humanize(warning.kind)}</Badge>
                <span className="flex-1">{warning.message}</span>
                {warning.severity === 'severe' && (
                  <Checkbox
                    checked={acknowledged.includes(warning.message)}
                    onCheckedChange={(checked) =>
                      setAcknowledged((current) =>
                        checked
                          ? [...current, warning.message]
                          : current.filter((m) => m !== warning.message),
                      )
                    }
                    label={tr('opd', 'acknowledge')}
                    className="shrink-0"
                  />
                )}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-sm">{tr('opd', 'new_prescription')}</CardTitle>
          {!readOnly && (
            <div className="flex flex-wrap gap-2">
              <RxSetsMenu items={items} onLoad={(drafts) => setItems(drafts.length ? drafts : [emptyDraft()])} />
              <Button variant="outline" size="sm" onClick={() => setItems((c) => [...c, emptyDraft()])}>
                <Plus className="h-4 w-4" /> {tr('opd', 'add_medicine')}
              </Button>
              <Button
                size="sm"
                onClick={() => issue.mutate()}
                loading={issue.isPending}
                disabled={named.length === 0 || blocked || withoutRoute.length > 0}
                title={
                  blocked
                    ? tr('opd', 'acknowledge_the_severe_warnings_first')
                    : withoutRoute.length > 0
                      ? tr('opd', 'choose_the_route_for_x', { value: withoutRoute.join(', ') })
                      : undefined
                }
              >
                {tr('opd', 'issue_and_print_2')}
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {items.map((item, index) => (
            <div key={index} className="grid gap-2 rounded-md border p-3 sm:grid-cols-12">
              <Field label={tr('opd', 'medicine')} className="sm:col-span-4">
                <DrugSearchInput
                  value={item.drugName}
                  readOnly={readOnly}
                  onType={(text) => updateItem(setItems, index, { drugName: text, productId: null })}
                  onChoose={(choice) =>
                    setItems((current) =>
                      current.map((row, i) =>
                        i === index
                          ? {
                              ...row,
                              productId: choice.productId,
                              drugName: choice.drugName,
                              strength: choice.strength ?? row.strength,
                              form: choice.form ?? row.form,
                              route: choice.route || row.route,
                              dose: choice.dose ?? row.dose,
                              frequency: choice.frequency ?? row.frequency,
                            }
                          : row,
                      ),
                    )
                  }
                />
              </Field>
              <Field label={tr('opd', 'strength')} className="sm:col-span-2">
                <Input
                  value={item.strength ?? ''}
                  readOnly={readOnly}
                  onChange={(e) => updateItem(setItems, index, { strength: e.target.value })}
                  placeholder={tr('opd', '500_mg')}
                />
              </Field>
              <Field label={tr('opd', 'dose')} className="sm:col-span-2">
                <Input
                  value={item.dose}
                  readOnly={readOnly}
                  onChange={(e) => updateItem(setItems, index, { dose: e.target.value })}
                  placeholder={tr('opd', '1_tablet')}
                />
              </Field>
              <Field label={tr('opd', 'frequency')} className="sm:col-span-2">
                <Input
                  value={item.frequency}
                  readOnly={readOnly}
                  onChange={(e) => updateItem(setItems, index, { frequency: e.target.value })}
                  placeholder={tr('opd', 'tds')}
                />
              </Field>
              <Field label={tr('opd', 'days')} className="sm:col-span-1">
                <Input
                  type="number"
                  min={0}
                  value={item.durationDays}
                  readOnly={readOnly}
                  onChange={(e) => updateItem(setItems, index, { durationDays: Number(e.target.value) })}
                />
              </Field>
              <Field label={tr('opd', 'form')} className="sm:col-span-2">
                <Input
                  value={item.form}
                  readOnly={readOnly}
                  onChange={(e) => setItems((current) => current.map((row, i) => (i === index ? withForm(row, e.target.value) : row)))}
                  placeholder={tr('opd', 'tablet')}
                />
              </Field>
              <Field label={tr('opd', 'route')} required className="sm:col-span-3">
                <Select
                  value={item.route}
                  disabled={readOnly}
                  onChange={(e) => updateItem(setItems, index, { route: e.target.value as PrescriptionDraft['route'] })}
                >
                  <option value="">{tr('opd', 'choose_the_route')}</option>
                  {ROUTE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={tr('opd', 'qty')} hint={tr('opd', 'blank_estimated_from_the_frequency_and_days')} className="sm:col-span-2">
                <Input
                  type="number"
                  min={0}
                  value={item.quantity || ''}
                  readOnly={readOnly}
                  onChange={(e) => updateItem(setItems, index, { quantity: Number(e.target.value) || 0 })}
                />
              </Field>
              <Field label={tr('opd', 'instructions')} className="sm:col-span-3">
                <Input
                  value={item.instructions}
                  readOnly={readOnly}
                  onChange={(e) => updateItem(setItems, index, { instructions: e.target.value })}
                  placeholder={tr('opd', 'after_meals')}
                />
              </Field>
              <div className="flex items-end pb-2 sm:col-span-1">
                <Checkbox
                  checked={item.isSos}
                  disabled={readOnly}
                  onCheckedChange={(checked) => updateItem(setItems, index, { isSos: Boolean(checked) })}
                  label={tr('opd', 'sos')}
                />
              </div>
              <div className="flex items-end sm:col-span-1">
                {!readOnly && items.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={tr('opd', 'remove_medicine')}
                    onClick={() => setItems((current) => current.filter((_, i) => i !== index))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}

          <Field label={tr('opd', 'advice_printed_on_the_prescription')}>
            <Textarea rows={2} value={notes} readOnly={readOnly} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </CardContent>
      </Card>

      {encounter.prescriptions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">{tr('opd', 'issued_this_visit')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {encounter.prescriptions.map((prescription) => {
              const sourceKeys = prescription.items.map((item) => (item.id ? `prescription_item:${item.id}` : null));
              const lockReason = authorisation.lockReasonKey(sourceKeys);
              return (
              <div key={prescription.id} className="flex items-center justify-between rounded-md border p-2 text-sm">
                <div>
                  <p>{prescription.items.map((i) => i.drugName).join(', ')}</p>
                  <p className="text-xs text-muted-foreground">{dateTime(prescription.issuedAt)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <SourceStateBadge sourceKeys={sourceKeys} />
                  <StatusBadge status={prescription.status} />
                  {prescription.status === 'issued' && !readOnly && lockReason && (
                    <Tooltip content={tr('opd', lockReason)}>
                      <span className="inline-flex">
                        <Button variant="ghost" size="sm" disabled>
                          {tr('opd', 'cancel')}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  {prescription.status === 'issued' && !readOnly && !lockReason && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCancelling(prescription.id)}
                    >
                      {tr('opd', 'cancel')}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={tr('opd', 'print')}
                    onClick={async () => {
                      const doc = await get<{ html: string }>(`/prescriptions/${prescription.id}/print`);
                      printDocument(doc.html);
                    }}
                  >
                    <Printer className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              );
            })}
          </CardContent>
        </Card>
      )}
      {cancelling && (
        <ReasonDialog
          title={tr('opd', 'cancel_prescription_title')}
          description={tr('opd', 'cancel_prescription_description')}
          minLength={3}
          maxLength={400}
          submitLabel={tr('opd', 'cancel_prescription')}
          destructive
          onSubmit={async (reason) => {
            try {
              await post(`/prescriptions/${cancelling}/cancel`, { reason });
              toast.success(tr('opd', 'prescription_cancelled'));
              setCancelling(null);
              onIssued();
            } catch (error) {
              toast.error(tr('opd', 'could_not_cancel_the_prescription'), describeError(error));
            }
          }}
          onClose={() => setCancelling(null)}
        />
      )}
    </div>
  );
}

function updateItem(
  setItems: React.Dispatch<React.SetStateAction<PrescriptionDraft[]>>,
  index: number,
  patch: Partial<PrescriptionDraft>,
): void {
  setItems((current) => current.map((item, i) => (i === index ? { ...item, ...patch } : item)));
}
